// assistantFlyContext.js — WHAT A SOLO FLY ASK IS ABOUT (Fly train 2;
// docs/FLY_MODE.md §3.6). The chat turn's own two keys, built the same way
// (functions/simoneAiTransport.js), ride a fly ask's `context`:
//   aiScope    the composer's chosen scope (functions/aiScope.js
//              aiScopePayload). While the Fly canvas holds the main area, its
//              `widgets` are the sections of the page ON SCREEN — each widget
//              or view with the params its view RESOLVED (`resolvedParams`
//              on the mounted body, the views contract in functions/
//              assistantAppViews.js). A site card that showed "Invest" when the
//              model named no site says `project: 'invest'`, so a follow-up
//              stays on the subject the card showed (the sites lane's finding,
//              2026-10-19: the next ask picked "Content and Marketing").
//   inspected  the regions the member pointed at (Inspect & ask, functions/
//              aiInspect.js — a composer pick's `inspect` facts), at most 3.
// The server reads both through the chat's own pickers (ai-boards askScope.js,
// AiBoardController pageContextOf): the ASK SCOPE and INSPECTED lines of the
// page prompt; the regions' figures reach the opening as ON SCREEN facts.
// A CALL's ask carries none (functions/assistantFly.js assistantFlyGenerate).
// The ask's TARGET (assistantFlyAskTarget below) stays on the page: its "Do
// it" hands it to the Tools turn as the structured `attached`.
//
// Frank note: el.call serializes each export on its own (FA204) — every
// export here is self-contained; a sibling is reached through this.call.

// The sections of the Fly page ON SCREEN as the ask scope's `widgets`: each
// widget or view of the open page, in its order, with its app and its params
// — the model's, then what its mounted view resolved over them. [] while the
// canvas is not shown (the scope keeps the home's own widgets then). A
// section whose params outgrow the server's bound (600 characters) goes
// without them; at most 12, the server's own cap.
export const assistantFlyOnScreen = function assistantFlyOnScreen(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r || !this.call('assistantFlyShown', r)) return []
  const page = this.call('assistantFlyPage', r)
  if (!page || !Array.isArray(page.items) || !page.items.length) return []
  const cells = (this.call('assistantFlyCells', page, r, 1) || []).filter(
    (c) => c && c.kind === 'widget' && c.id
  )
  if (!cells.length) return []
  // The mounted cards are keyed by their cell (functions/assistantFly.js
  // assistantFlyCells): one walk of the element tree finds them all.
  const keys = new Set(cells.map((c) => c.key))
  const top = typeof this.getRoot === 'function' ? this.getRoot() : null
  let cards = []
  try {
    cards =
      top && typeof top.lookdownAll === 'function'
        ? top.lookdownAll((c) => !!c && keys.has(c.key)) || []
        : []
  } catch (_) {
    cards = []
  }
  const plain = (v) => {
    try {
      const p = v && typeof v.parse === 'function' ? v.parse() : v
      return p && typeof p === 'object' && !Array.isArray(p) ? JSON.parse(JSON.stringify(p)) : null
    } catch (_) {
      return null
    }
  }
  const out = []
  for (const cell of cells) {
    const card = cards.find((c) => c && c.key === cell.key) || null
    const body = card && card.CardBody ? card.CardBody.content : null
    let resolved = null
    try {
      resolved = body ? plain(body.resolvedParams) : null
    } catch (_) {
      resolved = null
    }
    const params = { ...(plain(cell.params) || {}), ...(resolved || {}) }
    let json = ''
    try {
      json = JSON.stringify(params)
    } catch (_) {
      json = ''
    }
    out.push({
      id: String(cell.id),
      ...(cell.moduleId ? { app: String(cell.moduleId) } : {}),
      ...(json && json !== '{}' && json.length <= 600 ? { params } : {})
    })
    if (out.length >= 12) break
  }
  return out
}

// A solo fly ask's context → { context, picked }: `context` is
// `{ aiScope?, inspected? }` (null when there is nothing to say), `picked` the
// composer picks the ask takes (the regions) — the caller takes them out of
// the composer once the ask is placed, as a sent chat turn does.
export const assistantFlyAskContext = function assistantFlyAskContext(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return { context: null, picked: [] }
  const out = {}
  let scope = null
  try {
    scope = this.call('aiScopePayload', r)
  } catch (_) {
    scope = null
  }
  if (scope && typeof scope === 'object') {
    let shown = []
    try {
      shown = this.call('assistantFlyOnScreen', r) || []
    } catch (_) {
      shown = []
    }
    // An app scope keeps that app's sections, as it keeps its home widgets
    // (functions/aiScope.js).
    const appId = scope.kind === 'app' && scope.app && scope.app.id ? String(scope.app.id) : null
    const inScope = appId ? shown.filter((w) => w && w.app === appId) : shown
    out.aiScope = inScope.length ? { ...scope, widgets: inScope } : scope
  }
  const picks = Array.isArray(r.pickedElements) ? r.pickedElements : []
  const regions = picks.filter((p) => p && p.inspect && typeof p.inspect === 'object').slice(0, 3)
  if (regions.length) out.inspected = regions.map((p) => p.inspect)
  return { context: Object.keys(out).length ? out : null, picked: regions }
}

// THE TARGET of a solo fly ask — the project (and its board page) the ask
// was made on → `{ canvas: { projectId, boardPageId?, title? } }`, or null.
// The page keeps it (assistantFlyGenerate `page.target`) and its "Do it"
// (assistantFly.js assistantFlyHandoff) sends it as the Tools turn's
// structured `attached` — the server names it as the target of the ask
// (server attachedContext.js).
// WHY (dev, 2026-10-31, kestrel-assist, workspace W28 build c9d6dfbbd): on
// the Velvet Lantern canvas a Fly ask "Add a new empty page named QA Test
// One." made a page whose action read "Add page QA Test One". "Do it" sent
// those words alone; the Tools turn had no target, and the model took the
// Helmet app's "SEO page" action: a helmet row "/qa-test-one" was made, and
// Velvet Lantern did not change.
// Read at ASK time, in this order: the ask's own attachment (the canvas
// composer's `getSelectionAiContext().attached`), else the canvas project
// the route opens (`/w/<org>[/<ws>]/p/<key>`, or `/p/<key>` once the
// workspace prefix is stripped) — `root.data` only when it IS that project
// (its key is the route's: before the load it is still the previous one).
// Only what the server's sanitizer keeps, at its caps (120 / 200).
export const assistantFlyAskTarget = function assistantFlyAskTarget(root, attached) {
  const cap = (v, n) =>
    typeof v === 'string' || typeof v === 'number' ? String(v).trim().slice(0, n) : ''
  const shape = (projectId, boardPageId, title) =>
    projectId
      ? {
          canvas: {
            projectId,
            ...(boardPageId ? { boardPageId } : {}),
            ...(boardPageId && title ? { title } : {})
          }
        }
      : null
  const own = attached && typeof attached === 'object' && attached.canvas ? attached.canvas : null
  if (own && typeof own === 'object' && cap(own.projectId, 120)) {
    return shape(cap(own.projectId, 120), cap(own.boardPageId, 120), cap(own.title, 200))
  }
  const r = root && typeof root === 'object' ? root : null
  const fns = (this.context && this.context.functions) || {}
  if (!r || typeof r.currentPath !== 'string' || !r.currentPath) return null
  let key = ''
  try {
    const at =
      typeof fns.parseWorkspaceProjectPath === 'function'
        ? this.call('parseWorkspaceProjectPath', r.currentPath)
        : null
    const bare =
      typeof fns.stripWorkspacePrefix === 'function'
        ? this.call('stripWorkspacePrefix', r.currentPath)
        : r.currentPath
    const m = /^\/p\/([^/?#]+)/.exec(typeof bare === 'string' ? bare : '')
    key = at && at.key ? String(at.key) : m ? decodeURIComponent(m[1]) : ''
  } catch (_) {
    key = ''
  }
  const data = r.data && typeof r.data === 'object' && !r.projectNotFound ? r.data : null
  const loaded = data ? String(data.key || data.projectKey || '') : ''
  if (!key || !loaded || loaded.toLowerCase() !== key.toLowerCase()) return null
  const projectId = cap(data._id || data.id, 120)
  if (!projectId) return null
  let v2 = null
  try {
    v2 = typeof fns.getCanvasV2 === 'function' ? this.call('getCanvasV2') : null
  } catch (_) {
    v2 = null
  }
  const boardPageId = cap(v2 && v2.activePageId, 120)
  const board =
    boardPageId && v2 && Array.isArray(v2.pages)
      ? v2.pages.find((p) => p && p.id === boardPageId)
      : null
  return shape(projectId, boardPageId, cap(board && board.name, 200))
}
